import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays, MapPin, Compass, GraduationCap, Users, ArrowRight, Clock,
  UtensilsCrossed, Presentation, CheckCircle2, Mail, MessageCircle, Share2,
  Instagram, Facebook, Youtube, Globe, Phone, Car, Navigation, ClipboardCheck, Coffee,
} from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { api } from "../lib/api";

const ICONS = {
  compass: Compass, "graduation-cap": GraduationCap, users: Users, calendar: CalendarDays,
  "map-pin": MapPin, presentation: Presentation, clock: Clock, utensils: UtensilsCrossed,
  instagram: Instagram, facebook: Facebook, youtube: Youtube, share2: Share2, globe: Globe,
  mail: Mail, message: MessageCircle, phone: Phone,
};

const ACT_ICONS = {
  cena: UtensilsCrossed, evento_general: Users, taller: Presentation, sesion: Presentation,
  receso: Coffee, registro: ClipboardCheck, otro: Clock,
};

const HorizonBg = () => (
  <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 1440 640" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <path d="M-40 520 Q 720 380 1480 520" fill="none" stroke="#67BEC7" strokeOpacity="0.35" strokeWidth="2" />
    <path d="M-40 560 Q 720 430 1480 560" fill="none" stroke="#16A9D5" strokeOpacity="0.25" strokeWidth="2" />
    <path d="M-40 600 Q 720 480 1480 600" fill="none" stroke="#197FBA" strokeOpacity="0.15" strokeWidth="2" />
    <path d="M980 120 a180 180 0 0 1 180 180" fill="none" stroke="#67BEC7" strokeOpacity="0.3" strokeWidth="2" />
    <path d="M1010 120 a150 150 0 0 1 150 150" fill="none" stroke="#16A9D5" strokeOpacity="0.2" strokeWidth="2" />
    <path d="M1180 260 l40 40 -40 40" fill="none" stroke="#16A9D5" strokeOpacity="0.35" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="180" cy="140" r="60" fill="none" stroke="#67BEC7" strokeOpacity="0.2" strokeWidth="2" />
    <circle cx="180" cy="140" r="90" fill="none" stroke="#16A9D5" strokeOpacity="0.12" strokeWidth="2" />
  </svg>
);

const Countdown = ({ target }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return (
    <div className="mt-6 flex gap-3" data-testid="hero-countdown">
      {[["Días", d], ["Horas", h], ["Min", m], ["Seg", s]].map(([label, v]) => (
        <div key={label} className="rounded-xl border border-hz-gray/40 bg-white px-4 py-2 text-center shadow-sm">
          <p className="text-2xl font-extrabold text-hz-navy">{String(v).padStart(2, "0")}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-hz-blue">{label}</p>
        </div>
      ))}
    </div>
  );
};

const STEPS = [
  { n: 1, title: "Completa tus datos", text: "Nombre, contacto, fotografía y tu ubicación en la organización." },
  { n: 2, title: "Selecciona tus accesos y talleres", text: "Elige la cena especial y los talleres de tu interés." },
  { n: 3, title: "Recibe tu folio e instrucciones de pago", text: "Obtén tu folio único y la referencia para tu transferencia." },
  { n: 4, title: "Sube tu comprobante", text: "Cuando realices tu transferencia, sube tu comprobante para su revisión." },
];

const fmtDay = (a) => a.date_text || (a.day === "friday" ? "Viernes" : "Sábado");

export const LandingRenderer = ({ content }) => {
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState([]);

  useEffect(() => {
    api.get("/public/workshops").then((r) => setWorkshops(r.data.workshops || [])).catch(() => {});
  }, []);

  const c = content || {};
  const hero = c.hero || {};
  const about = c.about || {};
  const venue = c.venue || {};
  const program = c.program || {};
  const wsCfg = c.workshops || {};
  const priceCfg = c.pricing || {};
  const faq = c.faq || {};
  const contact = c.contact || {};

  const shownWorkshops = useMemo(() => {
    let list = workshops.filter((w) => w.schedule_complete !== false);
    if (wsCfg.sort_by === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    else list = [...list].sort((a, b) => String(a.starts_at || "").localeCompare(String(b.starts_at || "")));
    return list.slice(0, wsCfg.max_items || 12);
  }, [workshops, wsCfg.sort_by, wsCfg.max_items]);

  const goCta = (href) => {
    if (!href) return;
    if (href.startsWith("#")) document.getElementById(href.slice(1))?.scrollIntoView({ behavior: "smooth" });
    else if (href.startsWith("/")) navigate(href);
    else window.open(href, "_blank", "noopener,noreferrer");
  };

  const dayActivities = (day) => (program.activities || [])
    .filter((a) => a.active !== false && a.day === day)
    .sort((a, b) => (a.start_time || "99").localeCompare(b.start_time || "99") || (a.order || 0) - (b.order || 0));

  const faqs = (faq.items || []).filter((f) => f.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  const aboutCards = (about.cards || []).filter((x) => x.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  const socials = [
    contact.instagram_url && { icon: "instagram", url: contact.instagram_url, name: "Instagram" },
    contact.facebook_url && { icon: "facebook", url: contact.facebook_url, name: "Facebook" },
    contact.youtube_url && { icon: "youtube", url: contact.youtube_url, name: "YouTube" },
    contact.tiktok_url && { icon: "share2", url: contact.tiktok_url, name: "TikTok" },
    contact.website_url && { icon: "globe", url: contact.website_url, name: "Sitio web" },
    ...(contact.extra_socials || []).filter((s) => s.active !== false && s.url).sort((a, b) => (a.order || 0) - (b.order || 0)),
  ].filter(Boolean);

  return (
    <>
      {hero.enabled !== false && (
        <section id="inicio" className="relative overflow-hidden bg-hz-bg">
          <HorizonBg />
          {hero.bg_image_url && (
            <img src={hero.bg_image_url} alt={hero.bg_image_alt || ""} className="absolute inset-0 h-full w-full object-cover opacity-15" />
          )}
          <div className="relative mx-auto max-w-6xl px-4 pb-20 pt-14 sm:px-6 sm:pt-20">
            <div className="max-w-2xl animate-fade-up">
              <div className="mb-8 inline-flex rounded-2xl border border-hz-gray/40 bg-white p-4 shadow-sm">
                <Logo variant={hero.logo_variant === "title" ? "title" : hero.logo_variant === "icon" ? "icon" : "full"} className="h-16 sm:h-20" />
              </div>
              {hero.top_label && <p className="mb-3 text-xs font-bold uppercase tracking-widest text-hz-cyan" data-testid="hero-top-label">{hero.top_label}</p>}
              <h1 className="text-4xl font-extrabold tracking-tight text-hz-navy sm:text-5xl lg:text-6xl" data-testid="hero-title">{hero.title || "Horizonte 2026"}</h1>
              {hero.slogan && <p className="mt-3 text-lg font-bold uppercase tracking-widest text-hz-blue sm:text-xl" data-testid="hero-slogan">{hero.slogan}</p>}
              {hero.description && <p className="mt-5 max-w-xl text-base leading-relaxed text-hz-ink/80" data-testid="hero-description">{hero.description}</p>}
              <div className="mt-6 flex flex-wrap gap-4">
                {hero.date_text && (
                  <span className="inline-flex items-center gap-2 rounded-full border border-hz-gray/50 bg-white px-4 py-2 text-sm font-semibold text-hz-navy" data-testid="hero-date-placeholder">
                    <CalendarDays className="h-4 w-4 text-hz-cyan" /> {hero.date_text}{hero.time_text ? ` · ${hero.time_text}` : ""}
                  </span>
                )}
                {hero.venue_text && (
                  <span className="inline-flex items-center gap-2 rounded-full border border-hz-gray/50 bg-white px-4 py-2 text-sm font-semibold text-hz-navy" data-testid="hero-venue-placeholder">
                    <MapPin className="h-4 w-4 text-hz-cyan" /> {hero.venue_text}
                  </span>
                )}
              </div>
              {hero.show_countdown && hero.countdown_target && <Countdown target={hero.countdown_target} />}
              {hero.show_cta !== false && (
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  {hero.cta_text && (
                    <Button data-testid="hero-register-cta" onClick={() => goCta(hero.cta_href || "/registro")} className="h-12 bg-hz-cyan px-8 text-base font-bold text-white hover:bg-hz-blue">
                      {hero.cta_text} <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  )}
                  {hero.cta2_text && (
                    <Button data-testid="hero-program-cta" variant="outline" onClick={() => goCta(hero.cta2_href)} className="h-12 border-hz-navy px-8 text-base font-bold text-hz-navy hover:bg-hz-navy hover:text-white">
                      {hero.cta2_text}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {about.enabled !== false && (
        <section id="acerca" className="bg-white py-16 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            {about.label && <p className="text-xs font-semibold uppercase tracking-widest text-hz-blue">{about.label}</p>}
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-hz-navy sm:text-3xl lg:text-4xl" data-testid="about-title">{about.title}</h2>
            {about.text && <p className="mt-4 max-w-2xl whitespace-pre-line text-base leading-relaxed text-hz-ink/80" data-testid="about-text">{about.text}</p>}
            {aboutCards.length > 0 && (
              <div className="mt-10 grid gap-6 sm:grid-cols-3">
                {aboutCards.map((card) => {
                  const Icon = ICONS[card.icon] || Compass;
                  return (
                    <div key={card.title} className="rounded-2xl border border-hz-gray/40 bg-hz-bg p-6 shadow-sm transition-shadow duration-200 hover:shadow-md" data-testid={`about-card-${String(card.title).toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-hz-cyan/10"><Icon className="h-6 w-6 text-hz-cyan" /></div>
                      <h3 className="mt-4 text-lg font-bold text-hz-ink">{card.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-hz-ink/70">{card.text}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {venue.enabled && (
        <section id="sede" className="bg-hz-bg py-16 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-hz-blue">Fecha y sede</p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-hz-navy sm:text-3xl lg:text-4xl" data-testid="venue-title">{venue.title}</h2>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              <div className="space-y-4 rounded-2xl border border-hz-gray/40 bg-white p-6 shadow-sm" data-testid="venue-info-card">
                {venue.friday_date && (
                  <p className="flex items-center gap-2 text-sm font-semibold text-hz-ink" data-testid="venue-friday">
                    <CalendarDays className="h-4 w-4 shrink-0 text-hz-cyan" /> Viernes: {venue.friday_date}{(venue.friday_start || venue.friday_end) ? ` · ${venue.friday_start || ""}${venue.friday_end ? ` a ${venue.friday_end}` : ""} h` : ""}
                  </p>
                )}
                {venue.saturday_date && (
                  <p className="flex items-center gap-2 text-sm font-semibold text-hz-ink" data-testid="venue-saturday">
                    <CalendarDays className="h-4 w-4 shrink-0 text-hz-cyan" /> Sábado: {venue.saturday_date}{(venue.saturday_start || venue.saturday_end) ? ` · ${venue.saturday_start || ""}${venue.saturday_end ? ` a ${venue.saturday_end}` : ""} h` : ""}
                  </p>
                )}
                {venue.venue_name && <p className="flex items-center gap-2 text-sm font-bold text-hz-navy"><MapPin className="h-4 w-4 shrink-0 text-hz-cyan" /> {venue.venue_name}</p>}
                {venue.address && <p className="pl-6 text-sm text-hz-ink/70">{venue.address}{venue.city_state ? `, ${venue.city_state}` : ""}</p>}
                {venue.directions && <p className="pl-6 text-sm text-hz-ink/70">{venue.directions}</p>}
                {venue.parking_info && <p className="flex items-center gap-2 pl-0 text-sm text-hz-ink/70"><Car className="h-4 w-4 shrink-0 text-hz-blue" /> {venue.parking_info}</p>}
                {venue.phone && <p className="flex items-center gap-2 text-sm text-hz-ink/70"><Phone className="h-4 w-4 shrink-0 text-hz-blue" /> {venue.phone}</p>}
                {venue.maps_url && (
                  <div className="flex flex-wrap gap-3 pt-2">
                    <Button variant="outline" size="sm" onClick={() => window.open(venue.maps_url, "_blank", "noopener,noreferrer")} data-testid="venue-maps-btn" className="font-semibold">
                      <MapPin className="mr-1 h-4 w-4" /> Ver ubicación
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => window.open(venue.maps_url, "_blank", "noopener,noreferrer")} data-testid="venue-directions-btn" className="font-semibold">
                      <Navigation className="mr-1 h-4 w-4" /> Cómo llegar
                    </Button>
                  </div>
                )}
              </div>
              <div className="overflow-hidden rounded-2xl border border-hz-gray/40 bg-white shadow-sm">
                {venue.maps_embed_url ? (
                  <iframe src={venue.maps_embed_url} title="Mapa de la sede" className="h-full min-h-72 w-full" loading="lazy" data-testid="venue-map-embed" />
                ) : venue.image_url ? (
                  <img src={venue.image_url} alt={venue.image_alt || "Sede del evento"} className="h-full w-full object-cover" data-testid="venue-image" />
                ) : (
                  <div className="flex h-full min-h-72 items-center justify-center bg-hz-bg text-sm text-hz-ink/50">Mapa o imagen de sede</div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {program.enabled !== false && (
        <section id="programa" className={`${venue.enabled ? "bg-white" : "bg-hz-bg"} py-16 sm:py-24`}>
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            {program.label && <p className="text-xs font-semibold uppercase tracking-widest text-hz-blue">{program.label}</p>}
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-hz-navy sm:text-3xl lg:text-4xl" data-testid="program-title">{program.title}</h2>
            {program.intro_text && <p className="mt-4 max-w-2xl whitespace-pre-line text-base leading-relaxed text-hz-ink/80">{program.intro_text}</p>}
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {["friday", "saturday"].map((day) => {
                const acts = dayActivities(day);
                if (!acts.length) return null;
                return (
                  <div key={day} className="rounded-2xl border border-hz-gray/40 bg-white p-6 shadow-sm" data-testid={`program-${day}-card`}>
                    <span className="text-xs font-bold uppercase tracking-widest text-hz-cyan">{day === "friday" ? "Viernes" : "Sábado"}</span>
                    <div className="mt-4 space-y-4">
                      {acts.map((a) => {
                        const Icon = ACT_ICONS[a.type] || Clock;
                        return (
                          <div key={a.id} className="flex items-start gap-4" data-testid={`program-activity-${a.id}`}>
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-hz-navy/5"><Icon className="h-6 w-6 text-hz-navy" /></div>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-hz-ink/60">
                                {fmtDay(a)}{(a.start_time || a.end_time) ? ` · ${a.start_time || ""}${a.end_time ? ` a ${a.end_time}` : ""} h` : ""}
                                {a.room ? ` · ${a.room}` : ""}
                              </p>
                              <h3 className="text-lg font-bold text-hz-ink">{a.title}</h3>
                              {a.description && <p className="mt-1 text-sm text-hz-ink/70">{a.description}</p>}
                              {a.label && <span className="mt-1 inline-block rounded-full bg-hz-cyan/10 px-2.5 py-0.5 text-xs font-semibold text-hz-blue">{a.label}</span>}
                              {a.link && <a href={a.link} target="_blank" rel="noopener noreferrer" className="ml-2 text-xs font-semibold text-hz-cyan underline underline-offset-2">Más información</a>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-6 rounded-xl border border-hz-warning/30 bg-hz-warning/5 px-4 py-3 text-sm font-medium text-hz-ink" data-testid="program-access-note">
              Los accesos a cena y talleres dependen de la selección y confirmación de pago.
            </p>
          </div>
        </section>
      )}

      {wsCfg.enabled !== false && (
        <section id="talleres" className="bg-white py-16 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            {wsCfg.label && <p className="text-xs font-semibold uppercase tracking-widest text-hz-blue">{wsCfg.label}</p>}
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-hz-navy sm:text-3xl lg:text-4xl" data-testid="workshops-title">{wsCfg.title}</h2>
            {wsCfg.intro_text && <p className="mt-3 max-w-2xl text-sm font-medium text-hz-ink/60">{wsCfg.intro_text}</p>}
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {shownWorkshops.map((w) => (
                <div key={w.id} className="flex flex-col rounded-2xl border border-hz-gray/40 bg-hz-bg p-6 shadow-sm transition-shadow duration-200 hover:shadow-md sm:col-span-2 lg:col-span-3" data-testid={`workshop-card-${w.id}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-hz-turquoise/15"><Presentation className="h-6 w-6 text-hz-blue" /></div>
                  <h3 className="mt-4 text-lg font-bold text-hz-ink">{w.name}</h3>
                  {wsCfg.show_speaker !== false && w.speaker_name && <p className="mt-0.5 text-xs font-medium text-hz-ink/60">{w.speaker_name}</p>}
                  {w.schedule_text && <p className="mt-1 text-xs font-medium capitalize text-hz-ink/70">{w.schedule_text}{wsCfg.show_room !== false && w.room_name ? ` · ${w.room_name}` : ""}</p>}
                  {w.description && <p className="mt-2 flex-1 text-sm leading-relaxed text-hz-ink/70">{w.description}</p>}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {wsCfg.show_capacity !== false && (
                      <span className="rounded-full bg-hz-navy/5 px-3 py-1 text-xs font-semibold text-hz-navy">
                        {w.available > 0 ? `${w.available} lugares` : "Cupo lleno"}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {shownWorkshops.length === 0 && <p className="text-sm text-hz-ink/60" data-testid="workshops-empty">{wsCfg.empty_text || "Talleres por confirmar."}</p>}
            </div>
            {wsCfg.cta_text && (
              <div className="mt-8">
                <Button data-testid="workshops-cta" onClick={() => goCta(wsCfg.cta_href || "/registro")} className="h-11 bg-hz-cyan px-7 font-bold text-white hover:bg-hz-blue">{wsCfg.cta_text}</Button>
              </div>
            )}
          </div>
        </section>
      )}

      {priceCfg.enabled && (
        <section id="precios" className="bg-hz-bg py-16 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            {priceCfg.label && <p className="text-xs font-semibold uppercase tracking-widest text-hz-blue">{priceCfg.label}</p>}
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-hz-navy sm:text-3xl lg:text-4xl" data-testid="pricing-title">{priceCfg.title}</h2>
            <p className="mt-4 max-w-2xl whitespace-pre-line text-base leading-relaxed text-hz-ink/80" data-testid="pricing-intro">{priceCfg.intro_text || "Consulta el total correspondiente a tus selecciones durante el pre-registro."}</p>
            <div className="mt-6 space-y-2">
              {priceCfg.group_note && <p className="rounded-xl border border-hz-cyan/30 bg-hz-cyan/5 px-4 py-3 text-sm font-medium text-hz-ink" data-testid="pricing-group-note">{priceCfg.group_note}</p>}
              {priceCfg.payment_note && <p className="text-sm text-hz-ink/70">{priceCfg.payment_note}</p>}
              {priceCfg.qr_note && <p className="text-sm text-hz-ink/70">{priceCfg.qr_note}</p>}
            </div>
            {priceCfg.cta_text && (
              <div className="mt-6">
                <Button data-testid="pricing-cta" onClick={() => goCta(priceCfg.cta_href || "/registro")} className="h-11 bg-hz-cyan px-7 font-bold text-white hover:bg-hz-blue">{priceCfg.cta_text}</Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Cómo pre-registrarte (estático del sistema) */}
      <section className="bg-hz-bg py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-hz-blue">Cómo realizar el pre-registro</p>
          <h2 className="mt-2 text-2xl font-bold tracking-tight text-hz-navy sm:text-3xl lg:text-4xl" data-testid="steps-title">Cuatro pasos sencillos</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-2xl border border-hz-gray/40 bg-white p-6 shadow-sm" data-testid={`step-card-${s.n}`}>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-hz-cyan text-base font-extrabold text-white">{s.n}</span>
                <h3 className="mt-4 text-base font-bold text-hz-ink">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-hz-ink/70">{s.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 rounded-xl border border-hz-cyan/30 bg-hz-cyan/5 px-4 py-3 text-sm font-medium text-hz-ink" data-testid="steps-disclaimer">
            El pre-registro no confirma el acceso hasta que el pago sea revisado y confirmado por el equipo organizador.
          </p>
          <div className="mt-8">
            <Button data-testid="steps-register-cta" onClick={() => navigate("/registro")} className="h-12 bg-hz-cyan px-8 text-base font-bold text-white hover:bg-hz-blue">
              Iniciar pre-registro <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {faq.enabled !== false && faqs.length > 0 && (
        <section id="faq" className="bg-white py-16 sm:py-24">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            {faq.label && <p className="text-xs font-semibold uppercase tracking-widest text-hz-blue">{faq.label}</p>}
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-hz-navy sm:text-3xl lg:text-4xl" data-testid="faq-title">{faq.title}</h2>
            <Accordion type="single" collapsible className="mt-8">
              {faqs.map((f, i) => (
                <AccordionItem key={f.id || i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-sm font-bold text-hz-navy sm:text-base">{f.question}</AccordionTrigger>
                  <AccordionContent className="whitespace-pre-line text-sm leading-relaxed text-hz-ink/75">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {contact.enabled && (
        <section id="contacto" className="bg-hz-bg py-16 sm:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-hz-blue">Contacto</p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-hz-navy sm:text-3xl lg:text-4xl" data-testid="contact-title">{contact.title}</h2>
            {contact.text && <p className="mt-4 max-w-2xl whitespace-pre-line text-base leading-relaxed text-hz-ink/80">{contact.text}</p>}
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <div className="space-y-3 rounded-2xl border border-hz-gray/40 bg-white p-6 shadow-sm" data-testid="contact-info-card">
                {contact.team_name && <p className="text-sm font-bold text-hz-navy">{contact.team_name}</p>}
                {contact.schedule && <p className="flex items-center gap-2 text-sm text-hz-ink/70"><Clock className="h-4 w-4 text-hz-cyan" /> {contact.schedule}</p>}
                {contact.email && (
                  <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm font-semibold text-hz-blue hover:text-hz-cyan" data-testid="contact-email-link">
                    <Mail className="h-4 w-4 text-hz-cyan" /> {contact.email}
                  </a>
                )}
                {contact.whatsapp && (
                  <a href={`https://wa.me/${contact.whatsapp}${contact.whatsapp_message ? `?text=${encodeURIComponent(contact.whatsapp_message)}` : ""}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm font-semibold text-hz-blue hover:text-hz-cyan" data-testid="contact-whatsapp-link">
                    <MessageCircle className="h-4 w-4 text-hz-cyan" /> WhatsApp
                  </a>
                )}
                {contact.phone && (
                  <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-sm font-semibold text-hz-blue hover:text-hz-cyan" data-testid="contact-phone-link">
                    <Phone className="h-4 w-4 text-hz-cyan" /> {contact.phone}
                  </a>
                )}
              </div>
              {socials.length > 0 && (
                <div className="rounded-2xl border border-hz-gray/40 bg-white p-6 shadow-sm" data-testid="contact-socials-card">
                  <p className="text-sm font-bold text-hz-navy">Síguenos</p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {socials.map((s) => {
                      const Icon = ICONS[s.icon] || Share2;
                      return (
                        <a key={s.url + s.name} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.name} data-testid={`social-link-${String(s.name).toLowerCase().replace(/\s+/g, "-")}`}
                          className="flex h-11 w-11 items-center justify-center rounded-xl border border-hz-gray/40 bg-hz-bg text-hz-navy transition-colors hover:border-hz-cyan hover:text-hz-cyan">
                          <Icon className="h-5 w-5" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <div className="hidden items-center gap-3 rounded-2xl border border-hz-gray/40 bg-hz-bg p-5" aria-hidden="true">
        <CheckCircle2 className="h-6 w-6 shrink-0 text-hz-success" />
      </div>
    </>
  );
};
